import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./BurstHeart.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import LottieAnimator from "@Components/LottieAnimator";

/** Served from the S3 JSON bucket, like every other Lottie on the site. */
const BURST_HEART_FILE = "BurstHeart.json";

/**
 * The two halves of `BurstHeart.json`. They are not interchangeable: `like`
 * (frames 0-44) bursts outward, `unlike` (44-72) settles back. Playing the whole
 * file for either action runs both halves and ends on the wrong heart, which is
 * why this is handed to `LottieAnimator` as a marker rather than played straight.
 */
export type BurstHeartAction = "like" | "unlike";

/**
 * How much of the rendered box the resting heart occupies, **stroke included**.
 *
 * The artwork is a 200-unit canvas holding a 100-unit heart path carrying a 6.98-unit white
 * stroke centred on it, drawn at 62% — so what the eye actually spans is 106.98 units. The
 * stroke is not a rounding detail: leaving it out of this sizes the box ~7% too large, and the
 * heart visibly shrinks the moment the animation ends and the static icon takes back over.
 *
 * `WishlistHeartIcon` measures the same way — a 21.5-unit path with a 1.5 stroke inside a
 * 23-unit viewBox — which is why the two come out the same shape rather than merely close:
 * 23.00 x 20.50px either way, aspect 1.1220 both.
 */
const HEART_SHARE_OF_BOX = ((100 + 6.98) * 0.62) / 200;

/** `LottieAnimator` finds its container by id, so each burst on a list needs its own. */
let instanceCount = 0;

interface BurstHeartProps {
  action: BurstHeartAction;
  /** Width in px of the static heart this covers — the box is sized from it, not the reverse. */
  heartSize: number;
  /** Fires once the animation is on screen, so the caller can hide its own heart no earlier. */
  onReady?: () => void;
  onFinished: () => void;
}

/**
 * Plays one `BurstHeartAction` of the wishlist heart animation and calls `onFinished`.
 *
 * Port of the iOS app's `BurstHeartView`. The artwork is authored with the heart centred and
 * the burst filling the rest, so this renders larger than the heart it covers and must not be
 * clipped.
 */
const BurstHeart = ({ action, heartSize, onReady, onFinished }: BurstHeartProps) => {
  // Held in refs so a re-render with new callbacks doesn't tear the animation down: the ids
  // handed to `LottieAnimator` stay stable while always calling the latest prop.
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;
  const readyRef = useRef(onReady);
  readyRef.current = onReady;

  const handleFinished = useCallback(() => finishedRef.current(), []);
  const handleReady = useCallback(() => readyRef.current?.(), []);

  const [domId] = useState(() => {
    instanceCount += 1;
    return `burst-heart-${instanceCount}`;
  });

  // Reduced motion: no burst, just hand the card straight back to its own heart.
  const [reducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true,
  );

  useEffect(() => {
    if (reducedMotion) handleFinished();
  }, [reducedMotion, handleFinished]);

  if (reducedMotion) return null;

  const size = heartSize / HEART_SHARE_OF_BOX;

  return (
    <span className={styles.host} style={{ height: size, width: size }} aria-hidden="true">
      <LottieAnimator
        animationData={BURST_HEART_FILE}
        id={domId}
        renderer="svg"
        loop={false}
        marker={action}
        onComplete={handleFinished}
        onReady={handleReady}
        // Artwork or chunk never arrived. Hand the card back its own heart rather than leaving
        // it hidden behind an overlay that will never draw.
        onError={handleFinished}
      />
    </span>
  );
};

export default wrapperHOC(BurstHeart, {
  componentName: "BurstHeart-Global",
  showForChina: true,
});
